import type { ComponentType } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Box } from '@/components/ui/lib/box';
import { Container } from '@/components/ui/lib/container';
import { Flex } from '@/components/ui/lib/flex';
import { Skeleton } from '@/components/ui/lib/skeleton';
import { Stack } from '@/components/ui/lib/stack';
import SkeletonCardGrid from '@/components/ui/skeleton-card-grid';
import SkeletonRows from '@/components/ui/skeleton-rows';
import SkeletonTabBar from '@/components/ui/skeleton-tab-bar';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { resolvePageSkeletonKey } from '@/config/routes/page-skeletons';
import { DICTIONARY } from '@/config/dictionary';
import type { PageSkeletonKey } from '@/types/page-skeleton';
import type { DashboardPageSkeletonProps } from '@/app/types/route-pending';

const { dashboard, servicesPage, teamPage, adminSettingsPage } = DICTIONARY;

/**
 * The dashboard frame every page load keeps on screen: the real nav rail — readable and
 * clickable throughout — and the real page heading, which is static copy rather than data
 * and so is known before the fetch resolves. Only what the page has to load is greyed out.
 */
const DashboardPageSkeleton = ({ title, description, children }: DashboardPageSkeletonProps) => (
  <DashboardRouteLayout>
    <DashboardPageShell title={title} description={description}>
      <Box aria-busy="true" aria-label={DICTIONARY.common.loading}>
        {children}
      </Box>
    </DashboardPageShell>
  </DashboardRouteLayout>
);

/** Overview: five summary tiles over the recent-activity panel. */
const OverviewSkeleton = () => (
  <DashboardPageSkeleton title={dashboard.overview.heading} description={dashboard.overview.body}>
    <Stack gap="4">
      <SkeletonCardGrid count={5} columns={{ base: 1, sm: 2, lg: 5 }} height="170px" />
      <Box rounded="xl" borderWidth="1px" p="4" className="border-rim bg-surface">
        <Flex align="center" justify="space-between" gap="3" mb="4">
          <Skeleton height="1.75rem" width="12rem" rounded="md" />
          <Skeleton height="2rem" width="8rem" rounded="md" />
        </Flex>
        <SkeletonRows count={6} height="3rem" rounded="md" gap="2" />
      </Box>
    </Stack>
  </DashboardPageSkeleton>
);

/** Credit & Billing: two tabs, then the balance and payment-method pair over the thresholds card. */
const BillingSkeleton = () => (
  <DashboardPageSkeleton
    title={dashboard.billing.pageTitle}
    description={dashboard.billing.pageDescription}
  >
    <SkeletonTabBar count={2} />
    <Stack gap="4">
      <SkeletonCardGrid count={2} columns={{ base: 1, md: 2 }} height="13rem" gap="4" />
      <Skeleton height="9rem" rounded="2xl" />
    </Stack>
  </DashboardPageSkeleton>
);

/** Services: the save button, three tabs, then the service rows. */
const ServicesSkeleton = () => (
  <DashboardPageSkeleton title={servicesPage.heading} description={servicesPage.description}>
    <Flex justify="flex-end" mb="4" mt="-2">
      <Skeleton height="2rem" width="8rem" rounded="md" />
    </Flex>
    <SkeletonTabBar count={3} />
    <SkeletonRows count={4} height="72px" rounded="2xl" />
  </DashboardPageSkeleton>
);

/** Team: the search-and-invite toolbar over the member table. */
const TeamSkeleton = () => (
  <DashboardPageSkeleton title={teamPage.heading} description={teamPage.description}>
    <Flex justify="space-between" align="center" mt="-2" mb="4" gap="3">
      <Skeleton height="2rem" width="320px" maxW="100%" rounded="md" />
      <Skeleton height="2rem" width="7rem" rounded="md" />
    </Flex>
    <Skeleton height="40px" rounded="md" mb="3" />
    <SkeletonRows count={4} height="52px" rounded="md" />
  </DashboardPageSkeleton>
);

/** Wholesaler Settings: two tabs over the KYC status panel. */
const WholesalerSettingsSkeleton = () => (
  <DashboardPageSkeleton
    title={dashboard.wholesalerSettings.pageTitle}
    description={dashboard.wholesalerSettings.pageDescription}
  >
    <SkeletonTabBar count={2} />
    <Stack gap="4">
      <Skeleton height="10rem" rounded="2xl" />
      <SkeletonRows count={3} height="3.25rem" />
    </Stack>
  </DashboardPageSkeleton>
);

/** Admin Settings: four tabs over the config rows of whichever tab the URL asks for. */
const AdminSettingsSkeleton = () => (
  <DashboardPageSkeleton
    title={adminSettingsPage.pageTitle}
    description={adminSettingsPage.pageDescription}
  >
    <SkeletonTabBar count={4} width="7rem" />
    <SkeletonRows count={6} height="3.5rem" />
  </DashboardPageSkeleton>
);

/**
 * The Admin Settings sub-pages are each a single table behind their own heading, so they share
 * one shape and differ only in the copy above it.
 */
const RolesAdminSkeleton = () => (
  <DashboardPageSkeleton
    title={adminSettingsPage.rolesAdmin.pageTitle}
    description={adminSettingsPage.rolesAdmin.pageDescription}
  >
    <Skeleton height="40px" rounded="md" mb="3" />
    <SkeletonRows count={5} height="52px" rounded="md" />
  </DashboardPageSkeleton>
);

const ApiKeysAdminSkeleton = () => (
  <DashboardPageSkeleton
    title={adminSettingsPage.apiKeysAdmin.pageTitle}
    description={adminSettingsPage.apiKeysAdmin.pageDescription}
  >
    <Skeleton height="40px" rounded="md" mb="3" />
    <SkeletonRows count={5} height="52px" rounded="md" />
  </DashboardPageSkeleton>
);

const PermissionsAdminSkeleton = () => (
  <DashboardPageSkeleton
    title={adminSettingsPage.permissionsAdmin.pageTitle}
    description={adminSettingsPage.permissionsAdmin.pageDescription}
  >
    <Skeleton height="40px" rounded="md" mb="3" />
    <SkeletonRows count={5} height="52px" rounded="md" />
  </DashboardPageSkeleton>
);

/**
 * The catalogue and feature detail pages: a back link, the entity's name — which is data, so
 * it stays a placeholder — then its edit form.
 */
const AdminDetailSkeleton = () => (
  <DashboardPageSkeleton>
    <Skeleton height="1.25rem" width="6rem" rounded="md" mb="5" />
    <Skeleton height="2.25rem" width="16rem" rounded="lg" mb="6" />
    <SkeletonRows count={5} height="3rem" />
    <Flex justify="flex-end" mt="5">
      <Skeleton height="2.25rem" width="8rem" rounded="md" />
    </Flex>
  </DashboardPageSkeleton>
);

/** Initial Setup: the step rail over the current step's form. */
const SetupSkeleton = () => (
  <DashboardPageSkeleton title={dashboard.setup.heading} description={dashboard.setup.body}>
    <SkeletonCardGrid count={7} columns={{ base: 3, md: 7 }} height="3rem" gap="2" />
    <Box mt="6">
      <SkeletonRows count={4} height="3rem" />
      <Flex justify="space-between" mt="6">
        <Skeleton height="2.25rem" width="6rem" rounded="md" />
        <Skeleton height="2.25rem" width="8rem" rounded="md" />
      </Flex>
    </Box>
  </DashboardPageSkeleton>
);

/**
 * Settings, Branding, Numbers and Reports are placeholder pages — a single empty-state panel
 * under the heading. Their titles are per-page, so the heading stays a placeholder here.
 */
const PlaceholderPageSkeleton = () => (
  <DashboardPageSkeleton description={dashboard.emptyState.description}>
    <Skeleton height="2.25rem" width="14rem" rounded="lg" mb="6" />
    <Skeleton height="6rem" rounded="xl" />
  </DashboardPageSkeleton>
);

/** Anything under the dashboard shell that has no shape of its own yet. */
const GenericDashboardSkeleton = () => (
  <DashboardPageSkeleton>
    <Skeleton height="2.25rem" maxW="18rem" rounded="lg" mb="3" />
    <Skeleton height="1rem" maxW="28rem" rounded="md" mb="7" />
    <SkeletonRows count={5} />
  </DashboardPageSkeleton>
);

/** Sign-in, activation and account recovery: one centred card under a centred heading. */
const AuthCardSkeleton = () => (
  <Box
    as="section"
    minH="calc(100vh - 72px)"
    className="bg-subtle"
    aria-busy="true"
    aria-label={DICTIONARY.common.loading}
  >
    <Container maxW="lg" px={{ base: '6', sm: '8' }} py="16">
      <Flex direction="column" align="center" gap="3" mb="8">
        <Skeleton height="0.75rem" width="7rem" rounded="md" />
        <Skeleton height="2.25rem" width="18rem" maxW="100%" rounded="lg" />
        <Skeleton height="1rem" width="22rem" maxW="100%" rounded="md" />
      </Flex>
      <Box rounded="xl" borderWidth="1px" p="8" className="border-rim bg-page">
        <SkeletonRows count={3} height="3rem" />
        <Skeleton height="2.75rem" rounded="md" mt="6" />
      </Box>
    </Container>
  </Box>
);

/** The landing page: hero copy beside the auth card, then the sections below it. */
const LandingSkeleton = () => (
  <Box as="section" aria-busy="true" aria-label={DICTIONARY.common.loading}>
    <Container maxW="6xl" px={{ base: '6', sm: '8' }} py="16">
      <SkeletonCardGrid count={2} columns={{ base: 1, lg: 2 }} height="22rem" gap="10" />
      <Box mt="16">
        <SkeletonCardGrid count={3} columns={{ base: 1, md: 3 }} height="12rem" gap="6" />
      </Box>
    </Container>
  </Box>
);

const PAGE_SKELETONS: Record<PageSkeletonKey, ComponentType> = {
  landing: LandingSkeleton,
  authCard: AuthCardSkeleton,
  overview: OverviewSkeleton,
  billing: BillingSkeleton,
  services: ServicesSkeleton,
  team: TeamSkeleton,
  wholesalerSettings: WholesalerSettingsSkeleton,
  adminSettings: AdminSettingsSkeleton,
  rolesAdmin: RolesAdminSkeleton,
  apiKeysAdmin: ApiKeysAdminSkeleton,
  permissionsAdmin: PermissionsAdminSkeleton,
  adminDetail: AdminDetailSkeleton,
  setup: SetupSkeleton,
  placeholder: PlaceholderPageSkeleton,
  dashboard: GenericDashboardSkeleton,
};

/**
 * The router's default pending component: what stands in for a page while its lazy chunk, its
 * `beforeLoad` guard and the prefetch its `loader` awaits are still in flight. Every page gets
 * the skeleton that matches its own layout, so the wait reads as the page arriving rather than
 * as a spinner that could be anything.
 */
const RoutePending = () => {
  // While a navigation is pending `state.location` is already the destination, so the skeleton
  // is the incoming page's rather than the outgoing one's.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const PageSkeleton = PAGE_SKELETONS[resolvePageSkeletonKey(pathname)];

  return <PageSkeleton />;
};

export default RoutePending;
